// Shared availability utility for chargers and connectors
// Precedence: in_use > unavailable > available. Returns null if no status info.

const STATUS_MAP = { a: 'available', o: 'in_use', u: 'unavailable', f: 'unavailable' };

// Enhanced keyword lists with more variations
// Reduced aggressiveness of IN_USE keywords (removed 'active', 'connected', 'preparing' which were too broad)
const IN_USE_KEYWORDS = ['in_use', 'charging', 'occupied', 'in use', 'inuse', 'o', 'busy', 'session_active'];
const UNAVAILABLE_KEYWORDS = ['inactive', 'offline', 'unavailable', 'out_of_order', 'maintenance', 'faulted', 'error', 'down', 'u', 'f', 'fault'];
const AVAILABLE_KEYWORDS = ['available', 'free', 'idle', 'ready', 'a', 'open'];

const normalizeStatus = (raw) => {
  if (!raw) return '';
  const s = raw.toString().trim().toLowerCase();
  return STATUS_MAP[s] || s;
};

const matches = (s, list) => {
  if (!s) return false;
  const t = s.toLowerCase();
  return list.some(k => t === k || t.includes(k));
};

export const getChargerAvailability = (charger) => {
  if (!charger) return null;
  const collected = [];
  let found = false;
  let explicitAvailable = 0;
  let explicitInUse = 0;
  let explicitUnavailable = 0;

  // Debug Spring House specifically
  const isSpringHouse = charger?.name?.includes('Spring House');
  if (isSpringHouse) {
  }

  // ENHANCED: Check webapp devices/stations first (higher priority)
  if (Array.isArray(charger.webappData)) {
    for (const webappCharger of charger.webappData) {
      if (Array.isArray(webappCharger.devices)) {
        for (const device of webappCharger.devices) {
          if (Array.isArray(device.stations)) {
            for (const st of device.stations) {
              found = true;
              
              if (typeof st.isAvailable === 'boolean') {
                if (!st.isAvailable) {
                  collected.push('unavailable'); explicitUnavailable++;
                } else {
                  const stText = normalizeStatus(st.status || st.availability);
                  if (matches(stText, UNAVAILABLE_KEYWORDS)) { collected.push('unavailable'); explicitUnavailable++; }
                  else if (matches(stText, IN_USE_KEYWORDS)) { collected.push('in_use'); explicitInUse++; }
                  else { collected.push('available'); explicitAvailable++; }
                }
              } else if (st.status || st.availability) {
                const stText = normalizeStatus(st.status || st.availability);
                if (matches(stText, IN_USE_KEYWORDS)) { collected.push('in_use'); explicitInUse++; }
                else if (matches(stText, UNAVAILABLE_KEYWORDS)) { collected.push('unavailable'); explicitUnavailable++; }
                else if (matches(stText, AVAILABLE_KEYWORDS)) { collected.push('available'); explicitAvailable++; }
              }
            }
          }
        }
      }
    }
  }

  // Fallback: Devices / Stations hierarchy (original CMS data)
  if (!found && Array.isArray(charger.devices)) {
    for (const device of charger.devices) {
      let hasStation = false;
      if (Array.isArray(device.stations)) {
        for (const st of device.stations) {
          if (typeof st.isAvailable === 'boolean') {
            found = true; hasStation = true;
            if (!st.isAvailable) { collected.push('unavailable'); explicitUnavailable++; }
            else {
              const stText = normalizeStatus(st.status || st.availability);
              if (matches(stText, UNAVAILABLE_KEYWORDS)) { collected.push('unavailable'); explicitUnavailable++; }
              else if (matches(stText, IN_USE_KEYWORDS)) { collected.push('in_use'); explicitInUse++; }
              else { collected.push('available'); explicitAvailable++; }
            }
          } else if (st.status || st.availability) {
            found = true; hasStation = true;
            const stText = normalizeStatus(st.status || st.availability);
            if (matches(stText, IN_USE_KEYWORDS)) { collected.push('in_use'); explicitInUse++; }
            else if (matches(stText, UNAVAILABLE_KEYWORDS)) { collected.push('unavailable'); explicitUnavailable++; }
            else if (matches(stText, AVAILABLE_KEYWORDS)) { collected.push('available'); explicitAvailable++; }
          }
        }
      }
      if (!hasStation && (device.status || device.availability)) {
        found = true;
        const dText = normalizeStatus(device.status || device.availability);
        if (matches(dText, IN_USE_KEYWORDS)) { collected.push('in_use'); explicitInUse++; }
        else if (matches(dText, UNAVAILABLE_KEYWORDS)) { collected.push('unavailable'); explicitUnavailable++; }
        else if (matches(dText, AVAILABLE_KEYWORDS)) { collected.push('available'); explicitAvailable++; }
      }
    }
  }

  // Connectors
  if (Array.isArray(charger.connectors)) {
    for (const c of charger.connectors) {
      if (c.status || c.availability) {
        found = true;
        const cText = normalizeStatus(c.status || c.availability);
        if (matches(cText, IN_USE_KEYWORDS)) { collected.push('in_use'); explicitInUse++; }
        else if (matches(cText, UNAVAILABLE_KEYWORDS)) { collected.push('unavailable'); explicitUnavailable++; }
        else if (matches(cText, AVAILABLE_KEYWORDS)) { collected.push('available'); explicitAvailable++; }
      }
    }
  }

  // CMS specific fields
  if (collected.length === 0) {
    // Check for "in use" indicators in CMS fields
    const statusFields = [
      charger.status,
      charger.availability,
      charger.connector_status,
      charger.charger_status
    ].filter(Boolean);
    
    for (const statusField of statusFields) {
      const statusText = normalizeStatus(statusField);
      if (matches(statusText, IN_USE_KEYWORDS)) {
        found = true;
        collected.push('in_use');
        break;
      }
    }
    
    // Check CMS registration status and active connector for availability
    if (collected.length === 0 && charger.registration_status === 'Accepted' && charger.has_active_connector) {
      found = true;
      collected.push('available');
    }
  }

  // Direct fields
  if (collected.length === 0) {
    if (charger.availability) {
      found = true; const a = normalizeStatus(charger.availability);
      if (matches(a, IN_USE_KEYWORDS)) { collected.push('in_use'); explicitInUse++; }
      else if (matches(a, UNAVAILABLE_KEYWORDS)) { collected.push('unavailable'); explicitUnavailable++; }
      else if (matches(a, AVAILABLE_KEYWORDS)) { collected.push('available'); explicitAvailable++; }
    }
    if (charger.status) {
      found = true; const s = normalizeStatus(charger.status);
      if (matches(s, IN_USE_KEYWORDS)) { collected.push('in_use'); explicitInUse++; }
      else if (matches(s, UNAVAILABLE_KEYWORDS)) { collected.push('unavailable'); explicitUnavailable++; }
      else if (matches(s, AVAILABLE_KEYWORDS)) { collected.push('available'); explicitAvailable++; }
    }
  }

  // Debug Spring House final result
  if (isSpringHouse) {
  }

  // FIXED: Default to 'available' when no status info found instead of null
  // This ensures all chargers show up on the map
  if (!found || collected.length === 0) {
    return 'available';
  }
  // Explicit majority rules: choose most frequent explicit status if clear
  const totalExplicit = explicitAvailable + explicitInUse + explicitUnavailable;
  if (totalExplicit > 0) {
    const maxCount = Math.max(explicitAvailable, explicitInUse, explicitUnavailable);
    const winners = [];
    if (explicitAvailable === maxCount) winners.push('available');
    if (explicitInUse === maxCount) winners.push('in_use');
    if (explicitUnavailable === maxCount) winners.push('unavailable');
    if (winners.length === 1) return winners[0];
    // Tie breaker: unavailable > in_use > available (safety first)
    if (winners.includes('unavailable')) return 'unavailable';
    if (winners.includes('in_use')) return 'in_use';
    if (winners.includes('available')) return 'available';
  }
  // Fallback precedence
  if (collected.includes('unavailable')) return 'unavailable';
  if (collected.includes('in_use')) return 'in_use';
  if (collected.every(s => s === 'available')) return 'available';
  // Mixed ambiguous -> default to available instead of null
  return 'available';
};

export const getAvailabilityBadge = (status) => {
  if (status === 'available') return { label: 'Available', color: 'success.main', bg: 'rgba(76,175,80,0.08)' };
  if (status === 'in_use') return { label: 'In Use', color: 'warning.main', bg: 'rgba(255,193,7,0.12)' };
  if (status === 'unavailable') return { label: 'Unavailable', color: 'error.main', bg: 'rgba(244,67,54,0.10)' };
  return null;
};

export default getChargerAvailability;